import classNames from "components/ui/classNames";
import parse from "date-fns/parse";
import type { TooltipFormatterContextObject } from "highcharts";
import { useMemo } from "react";
import type { IntlShape } from "react-intl";
import type { IMarketCapHistory } from "types/graphqlTypes";

interface MarketCapitalizationTooltipProps {
  context: TooltipFormatterContextObject;
  marketCapitalizationHistory: IMarketCapHistory[];
  intl: IntlShape;
}

export default function MarketCapitalizationTooltip({
  context,
  marketCapitalizationHistory,
  intl,
}: MarketCapitalizationTooltipProps) {
  const percentageFormatter = useMemo(() => {
    return new Intl.NumberFormat("en-US", {
      style: "percent",
      maximumFractionDigits: 1,
    });
  }, []);

  if (marketCapitalizationHistory.length === 0 || !context.points || context.points.length === 0) {
    return null;
  }

  const marketCapitalizationHistoryValue = marketCapitalizationHistory[0].nodes;
  const node = (context.point as any).custom as (typeof marketCapitalizationHistoryValue)[number] | null | undefined;

  if (!context.key || !node || node.__typename !== "MarketCapHistoryItem") {
    return null;
  }

  const moneyFormatter = new Intl.NumberFormat(intl.locale, {
    notation: "compact",
    compactDisplay: "short",
    minimumSignificantDigits: 1,
    maximumSignificantDigits: 4,
  });

  return (
    <div className="z-10 flex flex-col space-y-3 p-2 text-xs">
      <span
        className={classNames(
          "inline-flex justify-center rounded-lg bg-slate-50 px-2 py-1 font-mono text-xs font-bold text-slate-600",
          "dark:bg-slate-900 dark:text-slate-300",
        )}
      >
        {intl.formatDate(parse(node.asOfDate, "yyyy-MM-dd", new Date()), {
          year: "numeric",
          month: "numeric",
          day: "numeric",
        })}
      </span>

      <div className="space-y-3">
        {context.points
          .sort((a, b) => {
            return (b.y || 0) - (a.y || 0);
          })
          .map(({ key, y, series, point }) => {
            const node = (point as any).custom as (typeof marketCapitalizationHistoryValue)[number] | null | undefined;
            if (!key || !y || !node || node.__typename !== "MarketCapHistoryItem") {
              return "";
            }

            const color = String(series.options.custom?.primaryColor);
            const lineColor = String(series.options.custom?.secondaryColor);

            const lastY = series.points.length > 0 ? series.points[series.points.length - 1].y : null;
            const returnSoFar = lastY ? lastY / y - 1 : null;

            return (
              <div key={color} className="flex items-center space-x-3">
                <span
                  className="rounded-md px-2 py-1 font-mono text-xs font-bold"
                  style={{
                    backgroundColor: color,
                    color: lineColor,
                  }}
                >
                  {series.name}
                </span>

                <span className={classNames("font-mono text-slate-800", "dark:text-slate-300")}>
                  {moneyFormatter.format(y)}
                </span>

                {returnSoFar !== null && (
                  <span
                    className={classNames("font-mono font-semibold", {
                      "text-rose-600 dark:text-rose-400": returnSoFar < 0,
                      "text-slate-600 dark:text-slate-400": returnSoFar === 0,
                      "text-emerald-600 dark:text-emerald-400": returnSoFar > 0,
                    })}
                  >
                    {percentageFormatter.format(returnSoFar)}
                  </span>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}
